export const dataURLToBlob = (dataURL) => {
  if (!dataURL || typeof dataURL !== "string") return null;
  const parts = dataURL.split(",");
  if (parts.length < 2) return null;
  const mimeMatch = parts[0].match(/data:(.*?);base64/);
  const mime = mimeMatch ? mimeMatch[1] : "application/octet-stream";
  const binary = atob(parts[1]);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
};

// Input can have { id, points, area, perimeter, areaUnit, perimeterUnit, isClosed, value }
export const normalizePolygonMinimal = (src) => {
  if (!src || !Array.isArray(src.points)) return null;

  // parse "A: 123 px² (P: 45 px)" if numeric fields missing
  const parseFromValue = (value) => {
    const out = {
      area: undefined,
      areaUnit: undefined,
      perimeter: undefined,
      perimeterUnit: undefined,
    };
    if (typeof value !== "string") return out;

    const toNum = (s) => {
      const n = parseFloat(String(s).replace(/,/g, ""));
      return Number.isFinite(n) ? n : undefined;
    };

    const areaMatch = value.match(/A:\s*([\d.,]+)\s*([a-zA-Zµμ²]+|px|mm)\b/i);
    const periMatch = value.match(/P:\s*([\d.,]+)\s*([a-zA-Zµμ²]+|px|mm)\b/i);

    if (areaMatch) {
      const n = toNum(areaMatch[1]);
      let u = (areaMatch[2] || "").replace(/\s+/g, "");
      if (u === "px" || u === "mm") u += "²"; // normalize
      out.area = n;
      out.areaUnit = u || undefined;
    }
    if (periMatch) {
      const n = toNum(periMatch[1]);
      let u = (periMatch[2] || "").replace(/\s+/g, "").replace("²", "");
      out.perimeter = n;
      out.perimeterUnit = u || undefined;
    }
    return out;
  };

  const derived =
    !Number.isFinite(src.area) ||
    !src.areaUnit ||
    !Number.isFinite(src.perimeter) ||
    !src.perimeterUnit
      ? parseFromValue(src.value)
      : {};

  return {
    id: String(src.id ?? Date.now()),
    points: src.points.map((p) => ({ x: +p.x, y: +p.y })),
    area: Number.isFinite(src.area) ? src.area : derived.area,
    perimeter: Number.isFinite(src.perimeter)
      ? src.perimeter
      : derived.perimeter,
    areaUnit: src.areaUnit ?? derived.areaUnit ?? "px²",
    perimeterUnit: src.perimeterUnit ?? derived.perimeterUnit ?? "px",
    isClosed: src.isClosed !== false,
    isImport: true, // default false if missing
  };
};

// analysisData.ts
export async function fetchAnalysisDoc(url) {
  if (!url) return null;

  // Plain fetch — front-end only
  const res = await fetch(url, {
    method: "GET",
    mode: "cors",
    cache: "no-cache",
    credentials: "omit",
  });

  if (!res.ok) {
    throw new Error(`fetchAnalysisDoc: ${res.status} ${res.statusText}`);
  }
  return res.json();
}

// Input can have { id, points, area, perimeter, areaUnit, perimeterUnit, isClosed, value }
export const normalizePolygonForCanvas = (src) => {
  if (!src || !Array.isArray(src.points)) return null;

  // parse "A: 123 px² (P: 45 px)" if numeric fields missing
  const parseFromValue = (value) => {
    const out = {
      area: undefined,
      areaUnit: undefined,
      perimeter: undefined,
      perimeterUnit: undefined,
    };
    if (typeof value !== "string") return out;

    const toNum = (s) => {
      const n = parseFloat(String(s).replace(/,/g, ""));
      return Number.isFinite(n) ? n : undefined;
    };

    const areaMatch = value.match(/A:\s*([\d.,]+)\s*([a-zA-Zµμ²]+|px|mm)\b/i);
    const periMatch = value.match(/P:\s*([\d.,]+)\s*([a-zA-Zµμ²]+|px|mm)\b/i);

    if (areaMatch) {
      const n = toNum(areaMatch[1]);
      let u = (areaMatch[2] || "").replace(/\s+/g, "");
      if (u === "px" || u === "mm") u += "²"; // normalize
      out.area = n;
      out.areaUnit = u || undefined;
    }
    if (periMatch) {
      const n = toNum(periMatch[1]);
      let u = (periMatch[2] || "").replace(/\s+/g, "").replace("²", "");
      out.perimeter = n;
      out.perimeterUnit = u || undefined;
    }
    return out;
  };

  const derived =
    !Number.isFinite(src.area) ||
    !src.areaUnit ||
    !Number.isFinite(src.perimeter) ||
    !src.perimeterUnit
      ? parseFromValue(src.value)
      : {};

  return {
    id: String(src.id ?? Date.now()),
    points: src.points.map((p) => ({ x: +p.x, y: +p.y })),
    area: Number.isFinite(src.area) ? src.area : derived.area,
    perimeter: Number.isFinite(src.perimeter)
      ? src.perimeter
      : derived.perimeter,
    areaUnit: src.areaUnit ?? derived.areaUnit ?? "px²",
    perimeterUnit: src.perimeterUnit ?? derived.perimeterUnit ?? "px",
    isClosed: false,
    // isClosed: src.isClosed !== false,
    isImport: true,
  };
};

/**
 * Compute differences between the first item in `selectedPolygons` and `selectedToCompare`.
 * Returns:
 * {
 *   area: { diff: number|null, unit: string|null },
 *   perimeter: { diff: number|null, unit: string|null }
 * }
 * Diff is signed: selected - compare. Units are passed through unchanged.
 */
//
// --- geometry helpers -------------------------------------------------
